<template>
  <div :class="['drawer-zone-row', 'drawer-zone-row--' + zone, { 'drawer-zone-row--flash': flashing }]">
    <span class="drawer-zone-row__label">{{ label }}</span>
    <div class="drawer-zone-row__value-wrap">
      <span
        v-if="!html"
        class="drawer-zone-row__value"
        :style="valueStyle"
      >{{ displayValue }}</span>
      <span
        v-else
        class="drawer-zone-row__value"
        :style="valueStyle"
        v-html="displayValue"
      ></span>
      <span v-if="fresh" class="drawer-zone-row__fresh">• {{ fresh }}</span>
      <slot name="action" />
    </div>
  </div>
</template>

<script>
// Fila de datos del drawer. 3 variantes por zona (DRAWERS-DESIGN-SPEC §8.1):
//   zone-1 (crítico): label uppercase arriba, valor grande 14px bold abajo.
//                     Opcional indicador de frescura "• hace 5s" y slot action.
//   zone-2 (estándar): label a la izquierda, valor a la derecha. Slot action.
//   zone-4 (admin): más comprimido, 12px, menor espacio vertical.
//
// Si `flashKey` cambia entre renders, el fondo del valor se ilumina 300ms
// como feedback de update en vivo.
export default {
  name: 'DrawerZoneRow',
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number, Boolean], default: '' },
    zone: { type: String, default: 'zone-2', validator: v => ['zone-1', 'zone-2', 'zone-4', 'zone-4-stacked'].indexOf(v) !== -1 },
    fresh: { type: String, default: '' },
    // Token de color para el valor (ej. 'var(--kln-status-online)').
    valueColor: { type: String, default: '' },
    // Si `true`, el valor se inyecta como innerHTML (permite <a>, <strong>, etc.).
    html: { type: Boolean, default: false },
    // Cuando cambia este key, se dispara la animación de flash.
    flashKey: { type: [String, Number, Boolean], default: '' },
  },
  data() {
    return { flashing: false }
  },
  computed: {
    displayValue() {
      var v = this.value
      if (v === null || v === undefined || v === '') return '—'
      // Defensa: jamas renderizar funciones/objetos. Vue 2 solo warnea en
      // dev si el prop type no matchea [String, Number, Boolean], pero el
      // render sigue, y el mustache hace toString — terminabamos imprimiendo
      // "function () { [native code] }" en casos raros (ej: paths donde
      // this.data resolvia a una referencia de metodo en vez del valor).
      // Ahora cualquier no-primitivo cae a em-dash.
      var t = typeof v
      if (t === 'function' || t === 'object' || t === 'symbol') return '—'
      // Belt-and-suspenders: si algun step previo hizo String(fn) y nos
      // paso el resultado como string, detectamos el marcador "[native
      // code]" y tambien caemos al em-dash.
      if (t === 'string' && v.indexOf('[native code]') !== -1) return '—'
      return v
    },
    valueStyle() {
      return this.valueColor ? { color: this.valueColor } : {}
    },
  },
  watch: {
    flashKey(newKey, oldKey) {
      // Primer mount (oldKey === undefined) no dispara flash; evita
      // "flashear todo al abrir el drawer". Solo updates reales.
      if (newKey === oldKey || oldKey === undefined || oldKey === '') return
      if (this._flashTimer) clearTimeout(this._flashTimer)
      this.flashing = true
      var self = this
      this._flashTimer = setTimeout(function() { self.flashing = false }, 450)
    },
  },
  beforeDestroy() {
    if (this._flashTimer) clearTimeout(this._flashTimer)
  },
}
</script>

<style scoped>
.drawer-zone-row {
  display: flex;
  font-size: 13px;
  color: var(--kln-text, rgba(255, 255, 255, 0.9));
  padding: 4px 12px;
  gap: 8px;
  transition: background 0.3s;
}

.drawer-zone-row__value-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
}

.drawer-zone-row__value {
  color: var(--kln-text, rgba(255, 255, 255, 0.9));
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-zone-row__fresh {
  color: var(--kln-text-dim, rgba(255, 255, 255, 0.4));
  font-size: 11px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Zona 1 — crítica: label encima uppercase, valor grande abajo. */
.drawer-zone-row--zone-1 {
  flex-direction: column;
  padding: 8px 12px;
  gap: 2px;
}

.drawer-zone-row--zone-1 .drawer-zone-row__label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.55));
}

.drawer-zone-row--zone-1 .drawer-zone-row__value-wrap {
  justify-content: flex-start;
  text-align: left;
}

.drawer-zone-row--zone-1 .drawer-zone-row__value {
  font-size: 14px;
  font-weight: 600;
  color: var(--kln-text, rgba(255, 255, 255, 0.92));
  white-space: normal;
}

/* Zona 2 — estándar: label a la izquierda, valor a la derecha. */
.drawer-zone-row--zone-2 {
  flex-direction: row;
  align-items: baseline;
  padding: 5px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.drawer-zone-row--zone-2 .drawer-zone-row__label {
  font-size: 11px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.45));
  flex-shrink: 0;
}

.drawer-zone-row--zone-2 .drawer-zone-row__value {
  font-size: 12px;
  font-weight: 500;
  color: var(--kln-text, rgba(255, 255, 255, 0.85));
}

/* Zona 4 — administrativa: más comprimido, 12px. */
.drawer-zone-row--zone-4 {
  flex-direction: row;
  align-items: baseline;
  padding: 3px 12px;
}

.drawer-zone-row--zone-4 .drawer-zone-row__label {
  font-size: 11px;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.4));
  flex-shrink: 0;
}

.drawer-zone-row--zone-4 .drawer-zone-row__value {
  font-size: 11px;
  font-weight: 400;
  color: var(--kln-text, rgba(255, 255, 255, 0.75));
}

/* Zona 4 — variante stacked: misma estructura visual que zona-1 (label
   arriba uppercase + valor debajo) pero con tipografías más pequeñas.
   Diseñada para campos administrativos con valores LARGOS (RFC, motor
   alfanumerico, observaciones, VIN) que no caben en una sola línea con
   `nowrap`+ellipsis. El operador prefiere ver el dato completo aunque
   ocupe 2 lineas, vs. ver "PLACAS ACTIV" cortado. */
.drawer-zone-row--zone-4-stacked {
  flex-direction: column;
  padding: 6px 12px;
  gap: 2px;
}

.drawer-zone-row--zone-4-stacked .drawer-zone-row__label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--kln-text-muted, rgba(255, 255, 255, 0.5));
}

.drawer-zone-row--zone-4-stacked .drawer-zone-row__value-wrap {
  justify-content: flex-start;
  text-align: left;
}

.drawer-zone-row--zone-4-stacked .drawer-zone-row__value {
  font-size: 12px;
  font-weight: 500;
  color: var(--kln-text, rgba(255, 255, 255, 0.85));
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}

/* Flash al cambiar el valor (telemetría viva). */
.drawer-zone-row--flash {
  animation: drawer-zone-flash 400ms ease-out;
}

@keyframes drawer-zone-flash {
  0%   { background: var(--kln-accent-soft, rgba(255, 255, 255, 0.12)); }
  100% { background: transparent; }
}
</style>
